<script setup>
import { ref, computed, watch, watchEffect, onUnmounted, provide, defineAsyncComponent } from "vue"
import { useEventListener, useBreakpoints, breakpointsTailwind } from "@vueuse/core"
import { useCanny } from '@/hooks/useCanny'
import { useRoute } from "vue-router"
import AdditionalBar from "./components/AdditionalBar.vue"
import { useGlobalStore } from "@/stores/GlobalStore"
import { useStrapiStore } from "@/stores/StrapiStore"
import { useConfigStore } from "@/stores/ConfigStore"
import { useUserStore } from "@/stores/UserStore"
import Header from "./Header/index.vue"
import AddressDialog from '@/components/Address/AddressDialog.vue'
import AddressAlert from '@/components/Address/AddressAlert.vue'

const ReactivateAccount = defineAsyncComponent(() => import('@/components/ReactivateAccount.vue'))
const AdminBanner = defineAsyncComponent(() => import("./components/AdminBanner.vue"))
const NotificationBar = defineAsyncComponent(() =>
  import("./components/NotificationBar.vue")
);
const Sidebar = defineAsyncComponent(() => import("./Sidebar/index.vue"))
const CannyDialog = defineAsyncComponent(() => import("./CannyDialog/index.vue"))

const globalStore = useGlobalStore()
const strapiStore = useStrapiStore()
const userStore = useUserStore()
const configStore = useConfigStore()
const route = useRoute()

const sidebar = computed(() => globalStore.sidebar);
const windowWidth = computed(() => globalStore.windowWidth);
const showAdminBanner = computed(() => globalStore.showAdminBanner);
const showSystemNotificationBar = computed(
  () => Object.keys(strapiStore.notificationStatus).length !== 0
);
const systemNotificationBar = computed(() => strapiStore.notificationStatus);
const notificationBarHeight = ref(40);
const showingAside = computed(() => {
  const excludedRoutes = [
    "export-batch",
    "create-batch",
    "AddTypes",
    "AddSides",
    "AddUpload",
    "AddCert",
  ];
  return !excludedRoutes.some(
    (routePart) => route.fullPath.includes(routePart) || route.name === routePart
  );
});

const showFeedbackDialog = ref({
  open: false,
});

useCanny()

const closeSidebar = () => {
  if (windowWidth.value < 1280) {
    globalStore.sidebar = false;
    localStorage.setItem("sidebar", JSON.stringify(globalStore.sidebar));
  }
};

const closeTabletSidebar = () => {
  if (windowWidth.value >= 768 && windowWidth.value < 1280) {
    if (globalStore.sidebar) {
      globalStore.sidebar = false;
      localStorage.setItem("sidebar", JSON.stringify(globalStore.sidebar));
    }
  }
}

const handleAddAddress = () => {
  globalStore.showAddressDialog = true
}

const effectiveBarHeight = computed(() =>
  windowWidth.value >= 768 ? 40 : notificationBarHeight.value
);

provide("showFeedbackDialog", showFeedbackDialog);
provide("showingAside", showingAside);
provide("closeSidebar", closeSidebar);
provide("showSystemNotificationBar", showSystemNotificationBar);
provide("systemNotificationBar", systemNotificationBar);
provide("notificationBarHeight", notificationBarHeight);
provide("effectiveBarHeight", effectiveBarHeight);


const mainContentScroll = ref(null)
const isCreditsBarMode = useBreakpoints(breakpointsTailwind).smaller('lg')

const lastScrollTops = new WeakMap()
let creditsRevealTimer = null
const clearCreditsTimer = () => {
  if (creditsRevealTimer) {
    clearTimeout(creditsRevealTimer);
    creditsRevealTimer = null
  }
}

const SCROLL_DELTA = 6
const TOGGLE_LOCK_MS = 350
let toggleLockTimer = null
let toggleLocked = false
const lockToggles = () => {
  toggleLocked = true
  if (toggleLockTimer) clearTimeout(toggleLockTimer)
  toggleLockTimer = setTimeout(() => { toggleLocked = false }, TOGGLE_LOCK_MS)
}
const setCreditsBarHidden = (hidden) => {
  if (globalStore.creditsBarHidden === hidden) return
  globalStore.creditsBarHidden = hidden
  lockToggles()
}

const scheduleCreditsAutoHide = (el) => {
  clearCreditsTimer()
  creditsRevealTimer = setTimeout(() => {
    if (isCreditsBarMode.value && !globalStore.creditsMenuOpen && !globalStore.creditsCollapseDisabled && (el?.scrollTop ?? 0) > 10) {
      setCreditsBarHidden(true)
    }
  }, 4000)
}

const onContentScroll = (e) => {
  const el = e.target
  if (!el || typeof el.scrollTop !== 'number') return

  if (el.closest?.('[data-credits-collapse-ignore]')) return

  const top = el.scrollTop
  const prev = lastScrollTops.get(el) ?? 0

  if (!isCreditsBarMode.value || globalStore.creditsMenuOpen) {
    lastScrollTops.set(el, top)
    clearCreditsTimer()
    globalStore.creditsBarHidden = false
    return
  }

  if (globalStore.creditsCollapseDisabled) {
    lastScrollTops.set(el, top)
    clearCreditsTimer()
    return
  }

  if (toggleLocked) { lastScrollTops.set(el, top); return }

  const delta = top - prev
  if (top > 10 && Math.abs(delta) < SCROLL_DELTA) return
  lastScrollTops.set(el, top)

  if (top <= 10) {
    clearCreditsTimer()
    setCreditsBarHidden(false)
  } else if (delta > 0 && top > 40) {
    clearCreditsTimer()
    setCreditsBarHidden(true)
  } else if (delta < 0) {
    setCreditsBarHidden(false)
    scheduleCreditsAutoHide(el)
  }
}

useEventListener(mainContentScroll, 'scroll', onContentScroll, { capture: true, passive: true })
useEventListener(document, 'scroll', onContentScroll, { capture: true, passive: true })

watch(() => globalStore.creditsMenuOpen, (open) => {
  if (open) { clearCreditsTimer(); globalStore.creditsBarHidden = false }
})

watch(isCreditsBarMode, (mobile) => {
  if (!mobile) { clearCreditsTimer(); globalStore.creditsBarHidden = false }
})

onUnmounted(() => {
  clearCreditsTimer()
  if (toggleLockTimer) clearTimeout(toggleLockTimer)
})

const sectionPaddingTop = computed(() => {
  const mainRowHeight = 48; // h-12
  const mobileBarHeight = isCreditsBarMode.value && configStore.config?.hasActiveSubscription
    ? (globalStore.creditsBarHidden ? 0 : 40)
    : 0;
  const headerHeight = mainRowHeight + mobileBarHeight;
  if (showSystemNotificationBar.value) {
    return `${effectiveBarHeight.value + headerHeight}px`;
  }
  if (showAdminBanner.value) {
    return `${40 + headerHeight}px`;
  }
  return `${headerHeight}px`;
});

const isBatchDetailsFlow = computed(
  () => route.matched?.length > 1 && route.matched[1].name === "BatchDetailsFlow"
)

const showAddressBanner = () => {
  const shouldShow = !isBatchDetailsFlow.value &&
    (!userStore.user?.address_1 || !userStore.user?.city || !userStore.user?.state || !userStore.user?.postal_code)
  globalStore.showAddressBanner = shouldShow
  return shouldShow
}

watchEffect(() => {
  const isHomepageOrRelated = [
    "Homepage",
    "Batches",
    "Inventory",
    "Connections",
  ].includes(route.name);

  if (!localStorage?.sidebar) {
    globalStore.sidebar = isHomepageOrRelated;
  }
});
</script>

<template>
  <NotificationBar />
  <AdminBanner />
  <section
    class="h-full flex pt-12 transition-all duration-300 overflow-hidden"
    :style="{ paddingTop: sectionPaddingTop }"
    :class="{
      'xl:!pl-72': sidebar,
      'md:pl-[60px]': showingAside,
      'md:!pl-72': sidebar,
    }"
  >
    <section class="main-layout" v-click-outside="closeTabletSidebar">
      <Header />
      <Sidebar />
    </section>
    <section
      id="main-content-scroll"
      ref="mainContentScroll"
      class="flex flex-col h-full flex-grow overflow-x-clip overflow-y-auto"
      :class="{
        'bg-[#F9FAFB]': route.path === '/connections/ebay/connect' || route.path === '/connections/ebay/connected' || route.path === '/connections/whatnot/connect'  || route.path === '/connections/whatnot/connected'
      }"
    >
      <AdditionalBar
        v-if="showAddressBanner()"
        :text="`Additional information is required. Our payment processor requires a complete address. Please enter your home or business address.`"
        :buttonText="`Add Address`"
        :buttonIcon="`heroicons:arrow-right`"
        @handleButtonClick="handleAddAddress"
      />
      <AddressDialog v-if="globalStore.showAddressDialog" />
      <AddressAlert v-if="globalStore.showAddressAlert" />
      <RouterView />
    </section>
  </section>

  <CannyDialog />

  <!-- Global Modal - Shows on all pages based on subscription status -->
  <section
    class="
      absolute top-0 left-0 w-full h-full flex-grow min-h-full flex flex-col z-[99] bg-no-repeat bg-cover
      bg-[url('@/assets/images/batches-frame.png')]
    "
    v-if="!configStore.reactivateAccountModal && !['SettingsPlans', 'Homepage'].includes(route.name)"
  >
    <ReactivateAccount />
  </section>
</template>
